import { Alert, AlertDescription } from "../../ui/alert";
import { Badge } from "../../ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { Button } from "../../ui/button";
import moment from "moment";
import React from "react";
import TrackPlayer from "./track-player";
import MicroPlayer from "../../common-components/micro-player/micro-player";
import AdminAlbumControls from "../../common-components/admin-album-controls/admin-album-controls";
import {
  Download,
  Edit,
  Calendar,
  Music,
  User,
  Mic2,
  Disc,
  ShieldCheck,
  Globe,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getConfig } from "../../../utils/permissions";
import StatusBadge from "../../common-components/status-badge";

export default function AlbumPreview({ album, setAlbum, tracks }) {
  const navigate = useNavigate();

  const editRelease = () => navigate("/edit-release/" + album._id);

  return (
    <div className="fade-in space-y-8 pb-10">
      {album.reviewComments && album.reviewComments !== "" && (
        <Alert variant="destructive">
          <AlertDescription>
            <div className="font-semibold mb-1">Rejection Reason:</div>
            <pre className="whitespace-pre-wrap font-sans text-sm">
              {album.reviewComments}
            </pre>
          </AlertDescription>
        </Alert>
      )}

      {/* Hero Section */}
      <div className="relative overflow-hidden rounded-3xl bg-background border">
        {/* Background Blur */}
        <div
          className="absolute inset-0 z-0 opacity-10 bg-cover bg-center blur-3xl scale-110"
          style={{ backgroundImage: `url(${album.artUrl})` }}
        />

        <div className="relative z-10 p-8 md:p-10 flex flex-col md:flex-row gap-8 md:gap-12 items-start md:items-center">
          {/* Album Art */}
          <div className="flex-shrink-0 relative group">
            <img
              src={album.artUrl}
              alt={album.title}
              className="w-52 h-52 md:w-64 md:h-64 rounded-2xl object-cover ring-1 ring-white/20 transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 rounded-2xl shadow-[inset_0_0_20px_rgba(0,0,0,0.1)] pointer-events-none" />
          </div>

          {/* Album Info */}
          <div className="flex-grow space-y-5">
            <div className="space-y-2">
              <div className="flex items-center gap-3 flex-wrap">
                <StatusBadge status={album.status} />
                <span className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                  <Disc className="w-4 h-4" /> {album.productionYear}
                </span>
                <span className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                  <Globe className="w-4 h-4" /> {album.language}
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
                {album.title}
              </h1>
              <div className="flex items-center gap-2 text-xl md:text-2xl text-muted-foreground font-light">
                <User className="h-5 w-5" />
                <span>{album.primaryArtist}</span>
                {album.secondaryArtist && (
                  <>
                    <span className="text-muted-foreground/40">&bull;</span>
                    <span className="text-base">{album.secondaryArtist}</span>
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 pt-2">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider flex items-center gap-1">
                  <Music className="w-3 h-3" /> Main Genre
                </p>
                <p className="font-semibold">{album.mainGenre}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider flex items-center gap-1">
                  <Mic2 className="w-3 h-3" /> Sub Genre
                </p>
                <p className="font-semibold">{album.subGenre || "-"}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Released
                </p>
                <p className="font-semibold">
                  {moment(album.releaseDate).format("MMM Do, YYYY")}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider flex items-center gap-1">
                  <Disc className="w-3 h-3" /> UPC
                </p>
                <p className="font-mono text-sm">{album.UPC || "Pending"}</p>
              </div>
            </div>

            {getConfig("privilege.can-edit-releases") && (
              <div className="pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={editRelease}
                  className="gap-2"
                >
                  <Edit className="h-4 w-4" /> Edit Metadata
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tracks Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between px-2">
          <h2 className="text-2xl font-bold tracking-tight">Tracks</h2>
          <Badge variant="secondary" className="px-3 py-1 text-sm">
            {tracks.length} Songs
          </Badge>
        </div>

        <Card className="overflow-hidden border-0">
          <Table>
            <TableHeader className="bg-muted/50">
              <TableRow>
                <TableHead className="w-[50px] text-center">#</TableHead>
                <TableHead className="min-w-[200px]">Title</TableHead>
                <TableHead className="min-w-[150px]">Artists</TableHead>
                <TableHead className="min-w-[150px]">Writer/Composer</TableHead>
                <TableHead className="min-w-[150px]">Details</TableHead>
                <TableHead className="w-[80px]">Audio</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tracks.map((track, index) => (
                <TableRow
                  key={index}
                  className="hover:bg-muted/30 transition-colors"
                >
                  <TableCell className="text-center font-medium text-muted-foreground">
                    {index + 1}
                  </TableCell>
                  <TableCell>
                    <div className="font-semibold text-base break-words line-clamp-2">
                      {track.title}
                    </div>
                    {track.ISRC && (
                      <div className="text-[10px] font-mono text-muted-foreground mt-1 bg-muted/60 px-1.5 py-0.5 rounded w-fit">
                        {track.ISRC}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1 text-sm">
                      <div className="font-medium line-clamp-2">
                        {track.primaryArtist}
                      </div>
                      {track.secondaryArtist && (
                        <div className="text-muted-foreground text-xs line-clamp-2">
                          {track.secondaryArtist}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1 text-sm text-muted-foreground">
                      <div className="line-clamp-1">
                        <span className="text-[10px] uppercase tracking-wide opacity-70">
                          Lyr:
                        </span>{" "}
                        {track.lyricist}
                      </div>
                      <div>
                        <span className="text-[10px] uppercase tracking-wide opacity-70">
                          Comp:
                        </span>{" "}
                        {track.composer}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <div className="flex gap-2 flex-wrap">
                      {track.explicit && (
                        <Badge
                          variant="destructive"
                          className="text-[10px] h-5 px-1.5"
                        >
                          Explicit
                        </Badge>
                      )}
                      <Badge
                        variant="outline"
                        className="text-[10px] h-5 px-1.5 font-normal"
                      >
                        {track.language}
                      </Badge>
                      <Badge
                        variant="outline"
                        className="text-[10px] h-5 px-1.5 font-normal"
                      >
                        {track.mainGenre}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    <TrackPlayer src={track.masterUrl} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {getConfig("privilege.can-can-download-master") && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-primary"
                          asChild
                        >
                          <a
                            href={track.masterUrl}
                            target="_blank"
                            rel="noreferrer"
                            download
                          >
                            <Download className="h-4 w-4" />
                          </a>
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
      {/* Admin Controls Section */}
      {getConfig("privilege.can-edit-releases") && (
        <Card className="border-l-4 border-l-primary/50 mt-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              Admin Controls
            </CardTitle>
            <CardDescription>
              Manage album status, ownership, and codes.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AdminAlbumControls
              album={album}
              setAlbum={setAlbum}
              tracks={tracks}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
